import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ActionTabs,
  ActionTabItem,
  LifeReadinessCategory,
  LifeReadinessQuestion,
  LifeReadinessSubcategory,
  LifeReadinessQuestionUserLevel,
  NUGGET_VARIANTS,
} from '@backrs_ui/common-ui';

import { appStrings as commonAppStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import AcademicFoundationsSticker from '@backrs_ui/grow-ui/public/images/stickers/lifeReadiness/academic-foundations.svg';
import AdaptabilitySticker from '@backrs_ui/grow-ui/public/images/stickers/lifeReadiness/adaptability.svg';
import CoreSkillsSticker from '@backrs_ui/grow-ui/public/images/stickers/lifeReadiness/core-skills.svg';
import FinancialPowerSticker from '@backrs_ui/grow-ui/public/images/stickers/lifeReadiness/financial-power.svg';
import OpportunityPathfindingSticker from '@backrs_ui/grow-ui/public/images/stickers/lifeReadiness/opportunity-pathfinding.svg';
import OwnershipHustleSticker from '@backrs_ui/grow-ui/public/images/stickers/lifeReadiness/ownership-hustle.svg';
import SocialCapitalSticker from '@backrs_ui/grow-ui/public/images/stickers/lifeReadiness/social-capital.svg';
import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { getBookmarkIdForNugget } from '@backrs_ui/grow-ui/src/pages/Bookmarks/pages/bookmarks.util';
import {
  usePostBookmarkMutation,
  useDeleteBookmarkMutation,
  useGetBookmarksQuery,
} from '@backrs_ui/grow-ui/src/redux/bookmarks/bookmarks.api';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';
import { PagePrompt } from '@backrs_ui/grow-ui/src/redux/pagePrompts/pagePrompts';
import { useRegisterPagePrompts } from '@backrs_ui/grow-ui/src/redux/pagePrompts/useRegisterPagePrompts';
import {
  openPlaylistListStep,
  setNuggetToAdd,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.slice';
import { useGetToolByToolIdQuery } from '@backrs_ui/grow-ui/src/redux/tools/tools.api';

import { Box, SxProps, Theme } from '@mui/material';

import { CategoryCard } from './molecules/CategoryCard';
import { DashboardHeader } from './molecules/DashboardHeader';
import { ScoreExplanationTable } from './molecules/ScoreExplanationTable';
import { areAllLifeReadinessQuestionsAnswered } from './utils/lifeReadinessQuestionnaire.util';
import { getCategoryProgress } from './utils/transformScoresToProgressBar';

const FIGMA_WIDTH = 936;
const GROW_URL = process.env.REACT_APP_GROW_URL;

const LIFE_READINESS_PROMPTS: readonly PagePrompt[] = [
  {
    promptButtonId: 'life_readiness_improve_my_profile',
    title: 'Improve my profile using my strengths',
  },
  {
    promptButtonId: 'life_readiness_succeed_in_school',
    title: 'How can they help me succeed in school?',
  },
  { promptButtonId: 'life_readiness_achieve_my_goals', title: 'Help me achieve my goals' },
  {
    promptButtonId: 'life_readiness_succeed_in_my_career',
    title: 'How can my strengths help me in my career?',
  },
];

type LifeReadinessDashboardProps = Readonly<{
  categories: LifeReadinessCategory[];
  subcategories: LifeReadinessSubcategory[];
  questions: LifeReadinessQuestion[];
  userLevels: LifeReadinessQuestionUserLevel[];
}>;

export const LifeReadinessDashboard = ({
  categories,
  subcategories,
  questions,
  userLevels,
}: LifeReadinessDashboardProps) => {
  const allQuestionsAnswered = areAllLifeReadinessQuestionsAnswered(questions, userLevels);

  const { data: lifeReadinessTool } = useGetToolByToolIdQuery('life_readiness');

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [showCopySuccess, setShowCopySuccess] = useState(false);
  const [postBookmark] = usePostBookmarkMutation();
  const [deleteBookmark] = useDeleteBookmarkMutation();
  const { data: bookmarks } = useGetBookmarksQuery(undefined);
  const toolBookmarkId = getBookmarkIdForNugget(
    bookmarks,
    lifeReadinessTool?.id,
    NUGGET_VARIANTS.TOOL,
  );

  useRegisterPagePrompts(allQuestionsAnswered ? LIFE_READINESS_PROMPTS : null);

  const handleSave = async () => {
    if (!lifeReadinessTool?.id) {
      return;
    }

    if (toolBookmarkId) {
      await deleteBookmark(toolBookmarkId);
    } else {
      await postBookmark({
        type: 'tool',
        itemId: lifeReadinessTool.id,
      });
    }
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(`${GROW_URL}${APP_ROUTE.LIFE_READINESS}`);
      setShowCopySuccess(true);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  const handleCreateTask = () => {
    if (!lifeReadinessTool) {
      return;
    }

    const toolAsNugget = {
      id: lifeReadinessTool.id,
      slug: lifeReadinessTool.tool_id,
      title: lifeReadinessTool.title,
      description: lifeReadinessTool.description,
      star_rating: 0,
      value_rating: 0,
      variant: 'tool' as const,
      is_browsable: false,
      tool_info: lifeReadinessTool,
      type: 'tool' as const,
    };

    dispatch(setNuggetToAdd(toolAsNugget));
    dispatch(openPlaylistListStep());
  };

  const actionButtons: ActionTabItem[] = [
    {
      onClick: handleSave,
      icon: toolBookmarkId ? 'BookmarkCheck' : 'Bookmark',
      tooltipHoverText: commonAppStrings.modules.actionTabs.bookmark,
    },
    {
      onClick: handleShare,
      icon: 'Share06',
      tooltipOnClickText: 'Link copied!',
      tooltipPosition: 'top',
      tooltipHoverText: commonAppStrings.modules.actionTabs.share,
    },
    {
      onClick: handleCreateTask,
      icon: 'ClipboardPlus',
      tooltipHoverText: commonAppStrings.modules.actionTabs.task,
    },
  ];

  const firstSubcategoryNameForCategory = (
    category: LifeReadinessCategory,
  ): LifeReadinessCategory['name'] => {
    const subcategoriesForCategory = subcategories
      .filter((subcategory) => subcategory.life_survey_category_id === category.id)
      .sort((a, b) => a.sequence - b.sequence);

    return subcategoriesForCategory[0].name.toLowerCase();
  };

  const getLifeReadinessStickerImage = (categorySlug: string): string => {
    const stickerMap: Record<string, string> = {
      'academic-foundations': AcademicFoundationsSticker,
      adaptability: AdaptabilitySticker,
      'core-skills': CoreSkillsSticker,
      'financial-power': FinancialPowerSticker,
      'opportunity-pathfinding': OpportunityPathfindingSticker,
      'ownership-hustle': OwnershipHustleSticker,
      'social-capital': SocialCapitalSticker,
    };

    return stickerMap[categorySlug] || '';
  };

  return (
    <Box sx={containerSx}>
      <Box sx={mainContentSx}>
        <Box sx={headerContainerSx}>
          <DashboardHeader
            rightContent={
              lifeReadinessTool ? (
                <ActionTabs
                  actions={actionButtons}
                  showCopySuccess={showCopySuccess}
                  onCopySuccessChange={setShowCopySuccess}
                />
              ) : undefined
            }
          />
        </Box>

        {/* ALL CARDS IN ONE GRID - KNOWLEDGE CARD FIRST ON SMALL SCREENS, LAST ON LARGE */}
        <Box sx={imageCardsGroupContainerSx}>
          {/* KNOWLEDGE CARD FIRST ON SMALL SCREENS */}
          <Box sx={knowledgeCardSx}>
            <ScoreExplanationTable />
          </Box>

          {/* ALL CATEGORY CARDS */}
          {categories.map((category) => (
            <CategoryCard
              key={category.id}
              imageSrc={getLifeReadinessStickerImage(category.slug)}
              title={category.name}
              description={category.description}
              progress={getCategoryProgress(category, subcategories, questions, userLevels)}
              onClick={() => {
                navigate(
                  `${APP_ROUTE.LIFE_READINESS}/${category.slug}/${firstSubcategoryNameForCategory(
                    category,
                  )}`,
                );
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const headerContainerSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
};

const mainContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  overflowY: 'auto',
  alignItems: 'left',
  pb: backrs.spacing['spacing-2xl'],
});

const containerSx: SxProps<Theme> = {
  display: {
    xs: 'block',
    md: 'flex',
  },
  flexDirection: {
    xs: 'column',
    md: 'row',
  },
  height: '100%',
  width: '100%',
};

const imageCardsGroupContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  containerType: 'inline-size',

  gap: backrs.spacing['spacing-xl'],

  gridTemplateColumns: 'repeat(auto-fill, minmax(min(234px, 100%), 1fr))',
  justifyItems: 'stretch',
  alignItems: 'start',

  width: '100%',
  maxWidth: `${FIGMA_WIDTH}px`,
  margin: '-2px auto',
  padding: '2px',
});

const knowledgeCardSx: SxProps<Theme> = {
  gridColumn: '1 / -1',
  '@container (min-width: 750px)': {
    gridColumn: 'span 2',
  },
  display: 'flex',
  flexDirection: 'column',
  order: 1,
};
